import { apiService } from '../../../components/api/apiService';
import type { WorkflowStartContract, WorkflowStartContractField } from '../chatagents/new/ChatIntents/intentDefinitionService';
import { isBlankWorkflowTemplate, isLeftoverDemoSample, parseWorkflowPayload, stringifyWorkflowPayload } from './publishSchemaBuilder';

const CONNECTION = 'backend_py';

type StudioStartContractRaw = {
  workflowId?: string;
  name?: string;
  description?: string;
  status?: string;
  revisionVersion?: number;
  inputNodeId?: string;
  inputNodeLabel?: string;
  triggerType?: string;
  replyMode?: string;
  reply_mode?: string;
  start?: {
    kind?: string;
    inputMode?: string;
    fields?: Array<{
      id?: string;
      key?: string;
      label?: string;
      dataType?: string;
      required?: boolean;
      defaultValue?: string;
      placeholder?: string;
      helpText?: string;
      options?: unknown[];
    }>;
    samplePayloadJson?: string | Record<string, unknown> | null;
  };
  runHints?: {
    payloadKeys?: string[];
    submitPath?: string;
    notes?: string;
  };
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function collectHintKeys(raw: StudioStartContractRaw): string[] {
  const start = asRecord(raw.start);
  const schema = asRecord(start?.schema);
  const props = asRecord(schema?.properties);
  const bags = [
    raw.runHints?.payloadKeys,
    (raw as Record<string, unknown>).requiredFields,
    (raw as Record<string, unknown>).payloadKeys,
    start?.requiredFields,
    start?.payloadKeys,
    start?.formFields,
    schema?.required,
    props ? Object.keys(props) : [],
  ];
  const keys: string[] = [];
  bags.forEach((bag) => {
    if (!Array.isArray(bag)) return;
    bag.forEach((item) => {
      if (typeof item === 'string' && item.trim()) keys.push(item.trim());
      const rec = asRecord(item);
      const key = String(rec?.key ?? rec?.id ?? rec?.name ?? '').trim();
      if (key) keys.push(key);
    });
  });
  return keys;
}

function mapField(raw: NonNullable<NonNullable<StudioStartContractRaw['start']>['fields']>[number]): WorkflowStartContractField {
  return {
    id: String(raw.id ?? '').trim(),
    key: String(raw.key ?? '').trim(),
    label: String(raw.label ?? raw.key ?? '').trim(),
    dataType: String(raw.dataType ?? 'text').trim() || 'text',
    required: Boolean(raw.required),
    defaultValue: raw.defaultValue != null ? String(raw.defaultValue) : undefined,
    placeholder: raw.placeholder != null ? String(raw.placeholder) : undefined,
    helpText: raw.helpText != null ? String(raw.helpText) : undefined,
    options: Array.isArray(raw.options) ? raw.options.map((item) => String(item).trim()).filter(Boolean) : [],
  };
}

function mapContract(raw: StudioStartContractRaw, channel: string): WorkflowStartContract {
  const rawFields = raw.start?.fields;
  const fields = Array.isArray(rawFields)
    ? rawFields.map(mapField).filter((field) => field.key || field.id)
    : [];
  const sample = parseWorkflowPayload(raw.start?.samplePayloadJson);
  const seen = new Set(fields.map((field) => field.key).filter(Boolean));
  const hintKeys = collectHintKeys(raw);
  const hasDeclared = fields.length > 0 || hintKeys.length > 0;
  const extraKeys = [
    ...hintKeys,
    ...((!hasDeclared || isBlankWorkflowTemplate(sample)) && !isLeftoverDemoSample(sample) ? Object.keys(sample) : []),
  ];
  extraKeys.forEach((item) => {
    const key = String(item || '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    fields.push({
      id: key,
      key,
      label: key,
      dataType: 'text',
      required: true,
      options: [],
    });
  });
  fields.forEach((field) => {
    if (field.key && (field.key in sample || extraKeys.includes(field.key))) field.required = true;
  });
  return {
    workflowId: String(raw.workflowId ?? '').trim(),
    name: String(raw.name ?? '').trim(),
    description: String(raw.description ?? '').trim(),
    status: String(raw.status ?? '').trim(),
    revisionVersion: raw.revisionVersion,
    inputNodeId: raw.inputNodeId,
    inputNodeLabel: raw.inputNodeLabel,
    triggerType: raw.triggerType,
    channel,
    start: {
      kind: raw.start?.kind,
      inputMode: raw.start?.inputMode,
      fields,
      samplePayloadJson: stringifyWorkflowPayload(raw.start?.samplePayloadJson),
    },
    runHints: raw.runHints,
  };
}

export type PublishedWorkflowContract = WorkflowStartContract & {
  replyMode: 'acknowledge' | 'wait_for_result';
  graphSource: 'draft' | 'revision';
};

export function resolveWorkflowReplyMode(contract: WorkflowStartContract, raw?: StudioStartContractRaw): 'acknowledge' | 'wait_for_result' {
  const blob = [
    raw?.replyMode,
    raw?.reply_mode,
    contract.start.kind,
    contract.runHints?.notes,
  ]
    .map((item) => String(item || '').toLowerCase())
    .join(' ');
  if (blob.includes('wait_for_result') || blob.includes('wait for result')) return 'wait_for_result';
  return 'acknowledge';
}

export function resolveWorkflowGraphSource(contract: WorkflowStartContract): 'draft' | 'revision' {
  if (contract.revisionVersion != null && Number(contract.revisionVersion) > 0) return 'revision';
  return String(contract.status || '').toLowerCase().includes('publish') ? 'revision' : 'draft';
}

export const studioWorkflowService = {
  async getStartContract(workflowId: string, channel = 'sandbox'): Promise<PublishedWorkflowContract> {
    const id = workflowId.trim();
    if (!id) throw new Error('Workflow id is required.');
    const resolvedChannel = channel.trim() || 'sandbox';
    const data = (await apiService.get(
      `/api/v1/studio/workflows/${encodeURIComponent(id)}/start-contract`,
      { channel: resolvedChannel },
      CONNECTION,
    )) as StudioStartContractRaw;
    const contract = mapContract(data || {}, resolvedChannel);
    const mapped = { ...contract, workflowId: contract.workflowId || id };
    return {
      ...mapped,
      replyMode: resolveWorkflowReplyMode(mapped, data),
      graphSource: resolveWorkflowGraphSource(mapped),
    };
  },
};
